import { useCallback, useEffect, useState } from 'react';
import jscookie from 'js-cookie';
import { getIm } from '@laigu/component/business/im';
import { useTranslateData, useFunction, useTranslate } from '@laigu/store';

const useTranslateMessage = props => {
  const { trackId, meId, translateLanguage, AutomaticTranslation, isOtherPage } = props;

  const im = getIm();
  const ent_id = jscookie.get('_enterprise_id');
  const translateMessage = useCallback(data => im.promiseEmit('translate', data), [im]);
  const { addTranslateData } = useTranslate();
  const { translateData } = useTranslateData();

  const [currentTranslateData, setCurrentTranslateData] = useState({});

  const changeCurrentTranslateData = (data, type, translated = {}, AutomaticTranslation) => {
    const { texts: msg } = data;
    let obj = {};
    if (type === 'translating') {
      msg.forEach(item => {
        obj[item.msg_id] = {
          msg_id: item.msg_id,
          translating: true
        };
      });
    } else if (type === 'translated') {
      const { texts = [] } = translated;
      if (texts.length) {
        texts.forEach(item => {
          if (item?.target_text) {
            obj[item.msg_id] = {
              msg_id: item.msg_id,
              translating: false,
              translated: item.target_text,
              targetLanguage: item.target_lang,
              sourceLanguage: item.source_lang,
              isSameLanguage: AutomaticTranslation && item.target_lang === item.source_lang
            };
          } else {
            obj[item.msg_id] = {
              msg_id: item.msg_id,
              translating: false,
              fail: true
            };
          }
        });
      }
    } else if (type === 'fail') {
      msg.forEach(item => {
        obj[item.msg_id] = {
          msg_id: item.msg_id,
          translating: false,
          fail: true
        };
      });
    }
    return obj;
  };

  const setAgentTranslateData = data => {
    setCurrentTranslateData(currentTranslateData => ({
      ...currentTranslateData,
      ...data
    }));
  };

  const handleTranslateMessage = useCallback(
    (data, track_id) => {
      setCurrentTranslateData(currentTranslateData => ({
        ...currentTranslateData,
        ...changeCurrentTranslateData(data, 'translating')
      }));
      return new Promise((resolve, reject) => {
        translateMessage(data)
          .then(res => {
            if (res.data) {
              setCurrentTranslateData(currentTranslateData => ({
                ...currentTranslateData,
                ...changeCurrentTranslateData(data, 'translated', res.data, AutomaticTranslation)
              }));
              if (AutomaticTranslation) {
                addTranslateData({ translated: res.data, track_id, isOtherPage: isOtherPage });
              }
              resolve(res.data);
            } else {
              setCurrentTranslateData(currentTranslateData => ({
                ...currentTranslateData,
                ...changeCurrentTranslateData(data, 'fail')
              }));
              reject();
            }
          })
          .catch(e => {
            setCurrentTranslateData(currentTranslateData => ({
              ...currentTranslateData,
              ...changeCurrentTranslateData(data, 'fail')
            }));
            reject();
          });
      });
    },
    [ent_id, meId, translateMessage, translateLanguage, addTranslateData, AutomaticTranslation, isOtherPage]
  );

  const handleAutoTranslate = useCallback(
    streams => {
      const reverseStreams = [...streams].reverse();
      if (reverseStreams.length) {
        let cacheData = [];
        let getData = [];
        reverseStreams.forEach(item => {
          if (
            item.action === 'message' &&
            item.from_type === 'client' &&
            item.content &&
            item.content_type === 'text'
          ) {
            const target = translateData.find(x => x.msg_id === item.id && x.targetLanguage === translateLanguage);
            if (target) {
              cacheData.push(target);
            } else {
              if (cacheData.length + getData.length < 20) {
                getData.push(item);
              }
            }
          }
        });
        let translateCache = {};
        cacheData.forEach(item => {
          translateCache[item.msg_id] = {
            ...item,
            isSameLanguage: AutomaticTranslation && item.sourceLanguage === item.targetLanguage
          };
        });
        setCurrentTranslateData(currentTranslateData => ({ ...currentTranslateData, ...translateCache }));
        if (getData.length) {
          let data = {
            source_lang: '',
            target_lang: translateLanguage,
            texts: []
          };
          getData.map(item => {
            data.texts.push({
              msg_id: item.id,
              source_text: item.content
            });
          });
          handleTranslateMessage(data, trackId);
        }
      }
    },
    [translateData, translateLanguage, handleTranslateMessage, trackId, AutomaticTranslation]
  );

  useEffect(() => {
    setCurrentTranslateData({});
  }, [trackId]);

  return {
    currentTranslateData,
    setAgentTranslateData,
    handleAutoTranslate,
    handleTranslateMessage
  };
};

export default useTranslateMessage;
